import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Satellite } from "lucide-react";

/**
 * Animated Starlink "live speed" chip for the Hero section.
 * Samples a new pseudo-random Mbit/s value between 320 and 400 every ~2s
 * and a latency value between 24 and 32 ms, with smooth interpolation in between.
 *
 * Respects prefers-reduced-motion: shows a static "400 Mbit/s · 28 ms" string.
 */

const MIN_SPEED = 320;
const MAX_SPEED = 400;
const MIN_LAT = 24;
const MAX_LAT = 32;
const SAMPLE_MS = 2000; // new target every 2 seconds
const TRANSITION_MS = 1500;
const SPARKLINE_LEN = 18;

const randBetween = (min, max) => min + Math.random() * (max - min);

// Gaussian-ish around the high end to "feel" like a real Starlink connection.
const sampleSpeed = () => {
  // 70% chance near top (370–400), 25% mid (340–370), 5% low (320–340)
  const r = Math.random();
  if (r < 0.7) return randBetween(370, MAX_SPEED);
  if (r < 0.95) return randBetween(340, 370);
  return randBetween(MIN_SPEED, 340);
};

const sampleLatency = () => {
  const r = Math.random();
  if (r < 0.75) return randBetween(MIN_LAT, 29);
  return randBetween(29, MAX_LAT);
};

const prefersReducedMotion = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
};

const LiveSpeedChip = ({ className = "", delay = 0 }) => {
  const reduced = useRef(prefersReducedMotion());
  const [speed, setSpeed] = useState(reduced.current ? 400 : 388);
  const [latency, setLatency] = useState(28);
  const [history, setHistory] = useState(() =>
    Array(SPARKLINE_LEN).fill(reduced.current ? 400 : 388),
  );

  useEffect(() => {
    if (reduced.current) return;
    let raf = 0;
    let currentSpeed = 388;
    let targetSpeed = sampleSpeed();
    let currentLat = 28;
    let targetLat = sampleLatency();
    let lastSampleAt = performance.now();

    const tick = (now) => {
      if (now - lastSampleAt >= SAMPLE_MS) {
        targetSpeed = sampleSpeed();
        targetLat = sampleLatency();
        lastSampleAt = now;
      }
      // Ease toward target (exponential smoothing).
      const k = 1 - Math.exp((now - lastSampleAt - SAMPLE_MS) / TRANSITION_MS);
      currentSpeed = currentSpeed + (targetSpeed - currentSpeed) * 0.08;
      currentLat = currentLat + (targetLat - currentLat) * 0.08;

      // Update React state only at a reasonable cadence (~10 Hz)
      if (Math.abs(currentSpeed - speed) > 0.5 || Math.abs(currentLat - latency) > 0.1) {
        setSpeed(currentSpeed);
        setLatency(currentLat);
      }
      // History sparkline: push every ~250ms
      if (Math.floor(now / 250) !== Math.floor((now - 16) / 250)) {
        setHistory((h) => [...h.slice(1), currentSpeed]);
      }
      // Silence the unused k warning while keeping eased fn around.
      void k;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displaySpeed = Math.round(speed);
  const displayLat = Math.round(latency);
  const pct = Math.max(0, Math.min(1, (speed - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)));

  // Build SVG polyline points for the mini sparkline.
  const w = 64;
  const h = 14;
  const points = history
    .map((v, i) => {
      const x = (i / (SPARKLINE_LEN - 1)) * w;
      const yPct = (v - MIN_SPEED) / (MAX_SPEED - MIN_SPEED);
      const y = h - Math.max(2, Math.min(h - 2, yPct * (h - 2) + 2));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.8, ease: "easeOut" }}
      data-testid="hero-live-speed-chip"
      aria-label={`Aktuelle Starlink Geschwindigkeit ${displaySpeed} Megabit pro Sekunde, Latenz ${displayLat} Millisekunden`}
      className={`absolute hidden md:flex glass rounded-2xl px-4 py-3 items-center gap-3 shadow-[0_10px_30px_rgba(0,0,0,0.12)] ${className}`}
    >
      <div className="w-9 h-9 rounded-xl bg-sky-600 text-white grid place-items-center relative">
        <Satellite className="w-4 h-4" />
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-white animate-ping"
        />
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white"
        />
      </div>
      <div className="leading-tight">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          <span className="text-emerald-600 font-semibold">LIVE</span>
          <span>· Starlink</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span
            data-testid="hero-live-speed-value"
            className="text-sm font-medium text-neutral-900 tabular-nums"
          >
            {displaySpeed}
          </span>
          <span className="text-[10px] text-neutral-500">Mbit/s</span>
          <span className="text-[10px] text-neutral-400">·</span>
          <span className="text-[10px] text-neutral-500 tabular-nums">{displayLat} ms</span>
        </div>
        <svg
          aria-hidden="true"
          viewBox={`0 0 ${w} ${h}`}
          width={w}
          height={h}
          className="mt-1 block"
        >
          <polyline
            points={points}
            fill="none"
            stroke="rgb(2 132 199)"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.85"
          />
        </svg>
        <div className="mt-1 h-[3px] w-16 rounded-full bg-neutral-200/70 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-sky-400 to-sky-600 rounded-full transition-[width] duration-500"
            style={{ width: `${Math.round(pct * 100)}%` }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default LiveSpeedChip;
